/** Sets the page's title, description, canonical URL, social tags and JSON-LD (seo.ts) while a Learn hub page is open. */
import { useEffect } from 'react';
import { BRAND } from '@platform/shared';
import { ARTICLES } from './content';
import type { LearnRoute } from './routes';
import { headFor, jsonLdText, type Head } from './seo';
import type { Faq } from './types';

/** Publisher logo in JSON-LD. */
export const LOGO = '/icons/icon-512.png';

/** Tags this hook owns, including the ones the build wrote into the static HTML. */
const OWNED = 'meta[name="description"],link[rel="canonical"],meta[name="robots"],meta[property^="og:"],meta[name^="twitter:"],meta[property^="article:"],script[type="application/ld+json"]';

function clear() {
  document.head.querySelectorAll(OWNED).forEach((el) => el.remove());
}

export function useHead(head: Head | null) {
  const key = head ? JSON.stringify(head) : '';
  useEffect(() => {
    if (!head) return;
    const origin = window.location.origin;
    clear();
    document.title = head.title;
    const add = (tag: 'meta' | 'link' | 'script', attrs: Record<string, string>, text?: string) => {
      const el = document.createElement(tag);
      for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
      if (text) el.textContent = text;
      document.head.appendChild(el);
    };
    const url = origin + head.path;
    const image = origin + head.image;
    add('meta', { name: 'description', content: head.description });
    add('link', { rel: 'canonical', href: url });
    if (head.noindex) add('meta', { name: 'robots', content: 'noindex' });
    for (const [p, v] of [
      ['og:type', head.type],
      ['og:site_name', `${BRAND.name} Academy`],
      ['og:title', head.title],
      ['og:description', head.description],
      ['og:url', url],
      ['og:image', image],
    ])
      add('meta', { property: p!, content: v! });
    for (const [n, v] of [
      ['twitter:card', 'summary_large_image'],
      ['twitter:title', head.title],
      ['twitter:description', head.description],
      ['twitter:image', image],
    ])
      add('meta', { name: n!, content: v! });
    if (head.publishedAt) add('meta', { property: 'article:published_time', content: head.publishedAt });
    if (head.updatedAt) add('meta', { property: 'article:modified_time', content: head.updatedAt });
    for (const o of head.jsonLd) add('script', { type: 'application/ld+json' }, jsonLdText(o));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  // Leaving the hub: the app's other pages have no description or canonical of their own.
  useEffect(
    () => () => {
      clear();
      document.title = BRAND.name;
    },
    [],
  );
}

/** useHead for a hub page (FAQs make an article's FAQPage JSON-LD once its body has loaded). */
export function usePageHead(route: LearnRoute, faqs?: readonly Faq[]) {
  useHead(headFor(route, { articles: ARTICLES, origin: window.location.origin, brand: BRAND.name, logo: LOGO, faqs }));
}
